import { useAllPermissions } from '@/hooks/usePermission'
import { PermissionTypes } from './permissions'

export interface MenuItemTypes {
	key: string
	label: string
	isTitle?: boolean
	icon?: string
	url?: string
	badge?: {
		variant: string
		text: string
	}
	parentKey?: string
	target?: string
	children?: MenuItemTypes[]
}

export enum MenuLinks {
	Login = '/auth/login',
	RecoverPassword = '/auth/recover-password',
	ManageUsers = '/administrator/manage-users',
	ManageRoles = '/administrator/manage-roles',
	ManageProfile = '/user/manage-profile',
	UserTwoFactorAuthentication = '/two-factor-authentication',
	ManageLookUps = '/administrator/manage-lookups',
	ManageLookUpValues = '/administrator/manage-lookup-values/:id/:lookupCodeId',
	ManageEmailLog = '/administrator/manage-email-log',
	Register = '/auth/register',
	BetaWelcome = '/auth/beta-welcome',
	RegisterSuccess = '/auth/register-success',
	ConfirmMail = '/auth/confirm-mail/:code/:userId',
	ManagePlatformLookUps = '/administrator/manage-platform-lookups',
	ManagePlatformLookUpValues = '/administrator/manage-platform-lookup-values/:id/:lookupCodeId',
	ViewAdministratorSubMenu = '/sub/administrator',
	ManageSettings = '/administrator/manage-settings',
	ManageEmailTemplate = '/administrator/manage-email-template',
	ManageLanguage = '/administrator/manage-language',
	ManageLanguageLocalizations = '/administrator/manage-language/localizations/:countryId',
	ViewSubMenus = '/sub/:subType',
	/** Authenticated tenant admin — browse/purchase (dashboard shell). */
	BuySubscriptionPlan = '/subscription-plan',
	/** Public marketing pricing page (no dashboard shell). */
	PublicPricing = '/pricing',
	SubscriptionStatus = '/subscription-status',
	PaymentConfirmation = '/payment-confirmation',
	ManageTenants = '/administrator/manage-tenants',
	EditTenantDetails = '/administrator/manage-tenants/edit/:id',
	MySubscriptions = '/my-subscriptions',
	MyCustomPlanRequests = '/my-custom-plan-requests',
	ManageSubscriptionPlans = '/administrator/manage-subscription-plans',
	ManagePromoCodes = '/administrator/manage-promo-codes',
	Campaigns = '/campaigns',
	ManageCustomPricing = '/administrator/manage-custom-pricing',
	ManageCustomPlanRequests = '/administrator/manage-custom-plan-requests',
	SubscriptionSales = '/administrator/subscription-sales',
	Transactions = '/administrator/transactions',
	TwoFactorVerification = '/auth/two-factor-verification/:sessionId',
	Dashboard = '/dashboard',
	OfflineGrant = '/offline-grant',
	Notifications = '/notifications',
	PaymentHistory = '/payment-history',
	PaymentMethods = '/payment-methods',
	MySubscriptionPacks = '/my-subscription-packs',
	CustomerDetail = '/administrator/customer/:tenantId',
	HelpCenter = '/help-center',
	HelpSupport = '/help-support',
	Referral = '/referral',
	ManageSqlDbdInstaller = '/administrator/manage-sqldbd-installer',
	/** SuperAdmin-only — manage roles/permissions for any organization. */
	ManageOrgPermissions = '/administrator/manage-org-permissions',
}

const MENU_ITEMS: MenuItemTypes[] = []

const getMenuItems = () => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const allPermissions = useAllPermissions()

	const permissionMapping: Record<string, PermissionTypes[]> = {
		administrator: [PermissionTypes.Permissions_Users_View, PermissionTypes.Permissions_Roles_View, PermissionTypes.Permissions_ManageLookUps_View, PermissionTypes.Permissions_CountryLocalization_View, PermissionTypes.Permissions_ManagePlatformLookUps_View, PermissionTypes.Permissions_ManageSettings_View],
		manage_email_log: [PermissionTypes.Permissions_EmailLog_View],
		manage_emailTemplates: [PermissionTypes.Permissions_EmailTemplates_View],
	}

	const menuWithPermissions = MENU_ITEMS.map((item) => {
		let canDisplay = true

		if (permissionMapping[item.key]) {
			canDisplay = permissionMapping[item.key].some((permission) => allPermissions[permission])
		}

		return {
			...item,
			canDisplay,
		}
	})

	return menuWithPermissions.filter((x) => x.canDisplay)
}

const findAllParent = (menuItems: MenuItemTypes[], menuItem: MenuItemTypes): string[] => {
	let parents: string[] = []
	const parent = findMenuItem(menuItems, menuItem.parentKey)

	if (parent) {
		parents.push(parent.key)
		if (parent.parentKey) {
			parents = [...parents, ...findAllParent(menuItems, parent)]
		}
	}
	return parents
}

const findMenuItem = (menuItems: MenuItemTypes[] | undefined, menuItemKey: MenuItemTypes['key'] | undefined): MenuItemTypes | null => {
	if (menuItems && menuItemKey) {
		for (let i = 0; i < menuItems.length; i++) {
			if (menuItems[i].key === menuItemKey) {
				return menuItems[i]
			}
			const found = findMenuItem(menuItems[i].children, menuItemKey)
			if (found) return found
		}
	}
	return null
}

export { getMenuItems, findAllParent, findMenuItem }

export { MENU_ITEMS }
